import { Layout } from '@/components/Layout';
import { Column } from '@/components/container/Container';
import Spacing from '@/components/container/Spacing';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { useEffect, useState } from 'react';
import { MdCreditCard, MdGroupAdd, MdOutlineRoofing, MdOutlineSportsBasketball } from 'react-icons/md';
import AccordionHeader from '../components/AccordionHeader';
import WelcomeHeader from '../components/WelcomeHeader';
import ChooseProgramContent from '../components/accordion-content/ChooseProgramContent';
import InviteTeamContent from '../components/content/InviteTeamContent';
import ProvideBillingInfoContent from '../components/content/ProvideBillingInfoContent';
import SetBillingContent from '../components/content/SetBillingContent';
import { GetOnboardingStepsResponse, OnboardingStep, stepCompleted } from '@/types';
import { UseQueryResult } from '@tanstack/react-query';
import { enumValues } from '@/common/common_utils';
import { root } from 'postcss';

type Props = {
	stepsQ: UseQueryResult<GetOnboardingStepsResponse, Error>;
}

const OnboardingStepsScreen = (props: Props) => {
	const completedSteps = props.stepsQ.isLoading ? "" : props.stepsQ.data?.Steps ?? "";

	const status = (step: OnboardingStep) => {
		return stepCompleted(completedSteps, step) ? 'completed' : 'pending';
	}

	const successIcon = <img alt="success" src="/images/svg/success.svg" />
	const onBoardingProcess = [
		{
			id: 'verify-account',
			title: 'Account Verified',
			status: status(OnboardingStep.AccountVerified),
			icon: successIcon,
			content: '',
		},
		{
			id: 'choose-program',
			title: stepCompleted(completedSteps, OnboardingStep.ChooseProgram) ? 'Travel program selected' : 'Choose a program',
			status: status(OnboardingStep.ChooseProgram),
			icon: stepCompleted(completedSteps, OnboardingStep.ChooseProgram) ? successIcon : <MdOutlineSportsBasketball />,
			content: <ChooseProgramContent isCompleted={stepCompleted(completedSteps, OnboardingStep.ChooseProgram)} />,
		},
		{
			id: 'set-billing',
			title: 'Set Billing Preferences',
			status: status(OnboardingStep.BillingPreferences),
			icon: stepCompleted(completedSteps, OnboardingStep.BillingPreferences) ? successIcon : <MdCreditCard />,
			content: <SetBillingContent />,
		},
		{
			id: 'provide-billing-info',
			title: stepCompleted(completedSteps, OnboardingStep.BillingInformation) ? 'Billing Information provided' : 'Provide Billing Information',
			status: status(OnboardingStep.BillingInformation),
			icon: stepCompleted(completedSteps, OnboardingStep.BillingInformation) ? successIcon : <MdOutlineRoofing />,
			content: <ProvideBillingInfoContent isFinished={stepCompleted(completedSteps, OnboardingStep.BillingInformation)} />,
		},
		{
			id: 'invite team',
			title: 'Invite your employee or team',
			status: status(OnboardingStep.InviteEmployees),
			icon: stepCompleted(completedSteps, OnboardingStep.InviteEmployees) ? successIcon : <MdGroupAdd />,
			content: <InviteTeamContent />,
		},
	];

	const [activeIndex, setActiveIndex] = useState(0);
	const firstIncompletedStep = enumValues(OnboardingStep).find((step) => !stepCompleted(completedSteps, step));

	useEffect(() => {
		setActiveIndex(firstIncompletedStep ? firstIncompletedStep - 1 : 1);
	}, [firstIncompletedStep, props.stepsQ.data]); // need to watch all steps also, because firstIncompletedStep may remain the same.

	return (
		<Layout>
			<Column className='onboarding'>
				<WelcomeHeader />
				<Spacing height="h-[20px]" />
				<Accordion
					activeIndex={activeIndex}
					// I have no idea why, but without onTabChange the accordeon doesn't update active tab when activeIndex1 change.
					onTabChange={(e) => {
						const index = e.index as number;
						if (index !== 0) setActiveIndex(index);
					}}
					pt={{
						root: { className: 'flex flex-col gap-3' },
						accordiontab: {
							root: { className: '!m-0 !p-0' },
						}
					}}
				>
					{onBoardingProcess.map((item, ind) => (
						<AccordionTab
							// disabled={!!(item.status === 'completed')}
							// disabled={true}
							key={item.id}
							header={
								<AccordionHeader
									icon={item.icon}
									title={item.title}
									titleCls={ind === 0 ? 'text-onSurface' : ''}
								/>
							}
							className={item.status === 'completed' ? 'step-completed' : ''}
							pt={{ headerIcon: { className: (ind == 0 && item.status === 'completed') && 'invisible' } }}
						>
							{item.content}
						</AccordionTab>
					))}
				</Accordion>
				{/* We don't have this in designgs: <div className="flex justify-end">
					<TextButton
						id="submit-for-review"
						label="Submit for Review"
						className={styles.reviewButton}
						href="/onboarding/account-pending"
					/>
				</div> */}
			</Column>
		</Layout>
	);
};

export default OnboardingStepsScreen;
